import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const QUIZ_KEY = 'knowfi_onboarded';

const QUESTIONS = [
  {
    id: 'challenge',
    question: "What's your biggest money challenge right now?",
    emoji: '👋',
    subtitle: 'Pick one — we\'ll point you to the right place.',
    options: [
      { label: '💳 I have debt I need to deal with',      value: 'debt',       route: '/gameplan'    },
      { label: '📈 I want to start investing',             value: 'invest',     route: '/howto'       },
      { label: '🛡️ I need an emergency fund',             value: 'emergency',  route: '/calculators' },
      { label: '🤷 I just don\'t understand money stuff', value: 'learn',      route: '/courses'     },
      { label: '📊 I want to track my finances',          value: 'track',      route: '/tracker'     },
      { label: '📰 I want to understand what markets do', value: 'markets',    route: '/brief'       },
    ],
  },
];

export function shouldShowOnboarding() {
  try { return !localStorage.getItem(QUIZ_KEY); } catch { return false; }
}

export function markOnboarded() {
  try { localStorage.setItem(QUIZ_KEY, '1'); } catch {}
}

export default function OnboardingQuiz({ onClose }) {
  const [step, setStep] = useState(0); // eslint-disable-line no-unused-vars
  const [selected, setSelected] = useState(null);
  const [done, setDone]       = useState(false);
  const navigate = useNavigate();

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const q = QUESTIONS[step];

  const handleSelect = (opt) => {
    setSelected(opt.value);
    setTimeout(() => {
      markOnboarded();
      setDone(true);
      setTimeout(() => {
        onClose?.();
        navigate(opt.route);
      }, 900);
    }, 300);
  };

  const skip = () => {
    markOnboarded();
    onClose?.();
  };

  return (
    <div style={s.overlay} onClick={skip}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {!done ? (
          <>
            {/* Progress dots */}
            <div style={s.dots}>
              {QUESTIONS.map((_, i) => (
                <div key={i} style={{ ...s.dot, ...(i === step ? s.dotActive : i < step ? s.dotDone : {}) }} />
              ))}
            </div>

            <div style={s.emoji}>{q.emoji}</div>
            <h2 style={s.question}>{q.question}</h2>
            {q.subtitle && <p style={s.subtitle}>{q.subtitle}</p>}

            <div style={s.options}>
              {q.options.map((opt) => (
                <button
                  key={opt.value}
                  style={{ ...s.option, ...(selected === opt.value ? s.optionSelected : {}) }}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                  {selected === opt.value && <span style={s.checkmark}>✓</span>}
                </button>
              ))}
            </div>

            <button style={s.skip} onClick={skip}>Skip — I'll look around →</button>
          </>
        ) : (
          <div style={s.doneState}>
            <div style={s.doneIcon}>🚀</div>
            <h2 style={s.doneTitle}>Got it — taking you there!</h2>
            <p style={s.doneText}>We'll personalize your experience based on your goals.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(5,10,20,0.85)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#1e293b', borderRadius: '20px', padding: '40px 36px',
    maxWidth: '480px', width: '100%', border: '1px solid #334155',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(56,189,248,0.08)',
    position: 'relative',
  },
  dots: { display: 'flex', gap: '6px', marginBottom: '24px', justifyContent: 'center' },
  dot: { width: '6px', height: '6px', borderRadius: '3px', background: '#334155', transition: 'all 0.2s' },
  dotActive: { background: '#38bdf8', width: '18px' },
  dotDone: { background: '#22c55e' },

  emoji: { fontSize: '40px', textAlign: 'center', marginBottom: '16px' },
  question: { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', textAlign: 'center', margin: '0 0 8px', lineHeight: 1.3 },
  subtitle: { color: '#64748b', fontSize: '14px', textAlign: 'center', margin: '0 0 24px' },

  options: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  option: {
    background: 'rgba(15,23,42,0.6)', border: '1px solid #334155', color: '#e2e8f0',
    padding: '14px 18px', borderRadius: '12px', fontSize: '15px', fontWeight: '500',
    cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    transition: 'all 0.15s',
  },
  optionSelected: {
    background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.4)',
    color: '#f1f5f9',
  },
  checkmark: { color: '#38bdf8', fontWeight: '800', fontSize: '16px' },

  skip: {
    background: 'transparent', border: 'none', color: '#475569',
    fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
    width: '100%', textAlign: 'center', padding: '8px',
  },

  doneState: { textAlign: 'center', padding: '20px 0' },
  doneIcon: { fontSize: '56px', marginBottom: '16px' },
  doneTitle: { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 8px' },
  doneText: { color: '#64748b', fontSize: '15px' },
};
